import { useEffect, useRef, useState } from 'react';

import type { ConversionState, ScanFieldKey } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import type { ConversionPreviewInfo } from '@/components/expenses/ConversionPreview';

import { ExpenseBasicInfoService } from '@/services/expense/ExpenseBasicInfoService';

import { ExpenseBasicInfoPresentation } from './ExpenseBasicInfoPresentation';

interface ExpenseBasicInfoContainerProps {
  title: string;
  setTitle: (value: string) => void;
  onTitleBlur?: () => void;
  /** The group's fixed currency — the target of any foreign-currency conversion. */
  groupCurrency: string;
  /** The currency the amount is being entered in. */
  enteredCurrency: string;
  onCurrencyChange: (value: string) => void;
  amount: string | undefined;
  setAmount: (value: string | undefined) => void;
  numericAmount: number;
  setNumericAmount: (value: number) => void;
  date: string;
  setDate: (value: string) => void;
  isLoading: boolean;
  category: string;
  setCategory: (category: string) => void;
  handleCategoryChange?: (category: string) => void;
  subcategory?: string;
  setSubcategory?: (subcategory: string | undefined) => void;
  setCategoryManuallyChanged?: (value: boolean) => void;
  showCategory?: boolean;

  // Foreign-currency conversion
  /** What the user typed, in `enteredCurrency`. */
  enteredAmount: number;
  conversion: ConversionState;
  preview: ConversionPreviewInfo;
  onManualRateChange: (rate: number | null) => void;
  isOfflineQueue: boolean;

  /** Fields the receipt scan filled; each shows the AI tag after its label. */
  aiTags?: ReadonlySet<ScanFieldKey>;
  /** The scan could not trust the receipt's currency. */
  checkCurrency?: boolean;
}

/**
 * Container component for expense basic info
 * Manages state and business logic using ExpenseBasicInfoService
 */
export function ExpenseBasicInfoContainer(
  props: ExpenseBasicInfoContainerProps
) {
  const { t } = useTranslation('expenses');

  // UI state
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showSubcategoryIcons, setShowSubcategoryIcons] = useState(false);
  const [selectedCategoryForIcons, setSelectedCategoryForIcons] =
    useState<string>('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const categoryRef = useRef<HTMLDivElement>(null);

  // Build category configuration
  const categoryConfig = ExpenseBasicInfoService.buildCategoryConfig(
    props.category,
    props.subcategory,
    t
  );

  // Get subcategory icons for selected category
  const subcategoryIcons = selectedCategoryForIcons
    ? ExpenseBasicInfoService.getSubcategoryIcons(selectedCategoryForIcons)
    : [];

  // Filter subcategory icons based on search
  const filteredSubcategoryIcons =
    ExpenseBasicInfoService.filterSubcategoryIcons(
      subcategoryIcons,
      subcategorySearch
    );

  // Handle click outside to close category selection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryRef.current &&
        !categoryRef.current.contains(event.target as Node)
      ) {
        ExpenseBasicInfoService.resetCategoryUI({
          setShowAllCategories,
          setShowSubcategoryIcons,
          setSelectedCategoryForIcons,
          setSubcategorySearch,
        });
      }
    };

    if (showAllCategories || showSubcategoryIcons) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAllCategories, showSubcategoryIcons]);

  // Handle amount change
  const handleAmountChange = (
    value: string | undefined,
    numericValue: number
  ) => {
    // Ensure we always update both values, trimming any trailing spaces (including non-breaking spaces)
    // Replace non-breaking spaces (char code 160) with regular spaces, then trim
    const cleanedValue = value?.replace(/\u00A0/g, ' ').trim() || '';
    props.setAmount(cleanedValue);
    props.setNumericAmount(numericValue || 0);
  };

  // Handle category selection
  const handleCategorySelect = (categoryValue: string) => {
    ExpenseBasicInfoService.handleCategorySelection(categoryValue, {
      setCategory: props.setCategory,
      setSubcategory: props.setSubcategory,
      handleCategoryChange: props.handleCategoryChange,
      setSelectedCategoryForIcons,
      setShowSubcategoryIcons,
      setShowAllCategories,
      setSubcategorySearch,
    });
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (subcategoryValue: string) => {
    ExpenseBasicInfoService.handleSubcategorySelection(
      selectedCategoryForIcons,
      subcategoryValue,
      {
        setCategory: props.setCategory,
        setSubcategory: props.setSubcategory,
        setShowSubcategoryIcons,
        setSelectedCategoryForIcons,
        setSubcategorySearch,
        setShowAllCategories,
      }
    );
  };

  // Handle back to categories
  const handleBackToCategories = () => {
    setShowSubcategoryIcons(false);
    setSelectedCategoryForIcons('');
    setSubcategorySearch('');
    setShowAllCategories(true);
  };

  // Handle show all categories
  const handleShowAllCategories = () => {
    setShowAllCategories(true);
  };

  // Handle cancel category selection
  const handleCancelCategorySelection = () => {
    setShowAllCategories(false);
  };

  return (
    <ExpenseBasicInfoPresentation
      // Form values
      title={props.title}
      setTitle={props.setTitle}
      onTitleBlur={props.onTitleBlur}
      groupCurrency={props.groupCurrency}
      enteredCurrency={props.enteredCurrency}
      onCurrencyChange={props.onCurrencyChange}
      amount={props.amount}
      onAmountChange={handleAmountChange}
      isLoading={props.isLoading}
      showCategory={props.showCategory || false}
      // Foreign-currency conversion
      enteredAmount={props.enteredAmount}
      conversion={props.conversion}
      preview={props.preview}
      onManualRateChange={props.onManualRateChange}
      isOfflineQueue={props.isOfflineQueue}
      aiTags={props.aiTags}
      checkCurrency={props.checkCurrency}
      date={props.date}
      // Category data
      category={props.category}
      subcategory={props.subcategory}
      categories={categoryConfig.categories}
      currentCategory={categoryConfig.currentCategory}
      // UI state
      showAllCategories={showAllCategories}
      showSubcategoryIcons={showSubcategoryIcons}
      selectedCategoryForIcons={selectedCategoryForIcons}
      subcategorySearch={subcategorySearch}
      setSubcategorySearch={setSubcategorySearch}
      filteredSubcategoryIcons={filteredSubcategoryIcons}
      // Handlers
      onCategorySelect={handleCategorySelect}
      onSubcategorySelect={handleSubcategorySelect}
      onShowAllCategories={handleShowAllCategories}
      onCancelCategorySelection={handleCancelCategorySelection}
      onBackToCategories={handleBackToCategories}
      // Refs
      categoryRef={categoryRef}
    />
  );
}
